import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { kpisVistoria, ciclosMonitoramento, comparacaoTemporal, type Vistoria } from "@/mocks/vistorias";
import { useInspectionsList } from "@/hooks";
import InspectionKpiCard from "./components/InspectionKpiCard";
import InspectionFilters from "./components/InspectionFilters";
import InspectionTable from "./components/InspectionTable";
import InspectionTimeline from "./components/InspectionTimeline";
import InspectionPriorityCard from "./components/InspectionPriorityCard";
import MonitoringCycleCard from "./components/MonitoringCycleCard";
import TemporalComparisonPanel from "./components/TemporalComparisonPanel";
import InspectionDetailDrawer from "./components/InspectionDetailDrawer";
import NewInspectionModal from "./components/NewInspectionModal";
import PageHeader from "@/components/shared/PageHeader";

type TabKey = "table" | "timeline" | "cycles" | "comparison";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "table", label: "Tabela", icon: "ri-table-line" },
  { key: "timeline", label: "Linha do Tempo", icon: "ri-history-line" },
  { key: "cycles", label: "Ciclos", icon: "ri-loop-left-line" },
  { key: "comparison", label: "Comparação", icon: "ri-line-chart-line" },
];

export default function Vistorias() {
  const [activeTab, setActiveTab] = useState<TabKey>("table");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selectedVistoria, setSelectedVistoria] = useState<Vistoria | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showPriority, setShowPriority] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const { items: allVistorias } = useInspectionsList();

  const filteredVistorias = useMemo(() => {
    let result = [...allVistorias];
    const search = (filters.search || "").toLowerCase().trim();
    if (search) {
      result = result.filter(
        (v) =>
          v.codigoCta.toLowerCase().includes(search) ||
          v.especie.toLowerCase().includes(search) ||
          v.nomePopular.toLowerCase().includes(search) ||
          v.tecnico.toLowerCase().includes(search) ||
          v.logradouro.toLowerCase().includes(search)
      );
    }
    if (filters.projeto) result = result.filter((v) => v.projeto === filters.projeto);
    if (filters.tipo) result = result.filter((v) => v.tipo === filters.tipo);
    if (filters.status) result = result.filter((v) => v.status === filters.status);
    if (filters.estado) result = result.filter((v) => v.estadoFitossanitario === filters.estado);
    if (filters.risco) result = result.filter((v) => v.classeRisco === filters.risco);
    if (filters.tecnico) result = result.filter((v) => v.tecnico === filters.tecnico);
    return result;
  }, [filters, allVistorias]);

  const handleFilterChange = (key: string, value: string) => {
    if (key === "apply") return;
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => setFilters({});

  const handleAction = (action: string, v: Vistoria) => {
    console.log(`[Vistorias] Ação: ${action} - ${v.id}`);
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-foreground-400">
        <Link to="/" className="hover:text-foreground-600 transition-colors whitespace-nowrap">Dashboard</Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="text-foreground-700 font-medium whitespace-nowrap">Vistorias</span>
      </nav>

      {/* Header */}
      <PageHeader
        title="Vistorias"
        subtitle="Acompanhe avaliações técnicas, remedições, risco, fitossanidade e histórico do CTA."
      >
        <button onClick={() => setShowNewModal(true)} className="btn-primary text-xs whitespace-nowrap">
          <i className="ri-add-line"></i>Nova Vistoria
        </button>
        <button className="btn-secondary text-xs whitespace-nowrap">
          <i className="ri-loop-left-line"></i>Criar Ciclo
        </button>
        <button className="btn-outline text-xs whitespace-nowrap">
          <i className="ri-upload-line"></i>Importar
        </button>
        <button className="btn-outline text-xs whitespace-nowrap">
          <i className="ri-download-line"></i>Exportar
        </button>
        <div className="relative">
          <button onClick={() => setMoreOpen(!moreOpen)} className="btn-outline text-xs whitespace-nowrap">
            <i className="ri-more-2-fill"></i>Mais
          </button>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)}></div>
              <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl border border-background-200/70 shadow-lg py-1.5 min-w-[200px]">
                <button onClick={() => { setShowPriority(!showPriority); setMoreOpen(false); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-alert-line text-foreground-400"></i>{showPriority ? "Ocultar" : "Mostrar"} alertas
                </button>
                <button onClick={() => { setMoreOpen(false); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-printer-line text-foreground-400"></i>Imprimir lista
                </button>
                <button onClick={() => { setMoreOpen(false); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-share-line text-foreground-400"></i>Compartilhar
                </button>
              </div>
            </>
          )}
        </div>
      </PageHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InspectionKpiCard value={kpisVistoria.realizadas} label="Realizadas" icon="ri-check-double-line" color="primary" />
        <InspectionKpiCard value={kpisVistoria.pendentesRevisao} label="Pendentes revisão" icon="ri-file-search-line" color="warning" />
        <InspectionKpiCard value={kpisVistoria.vencidas} label="Vencidas" icon="ri-timer-line" color="danger" />
        <InspectionKpiCard value={kpisVistoria.riscoCritico} label="Risco crítico" icon="ri-alert-line" color="danger" />
        <InspectionKpiCard value={kpisVistoria.vistoriasMes} label="Este mês" icon="ri-calendar-check-line" color="accent" />
        <InspectionKpiCard value={kpisVistoria.arvoresRemonitoradas} label="Remonitoradas" icon="ri-refresh-line" color="secondary" />
        <InspectionKpiCard value={kpisVistoria.fotosAnexadas} label="Fotos" icon="ri-camera-line" color="accent" />
        <InspectionKpiCard value={kpisVistoria.pareceresPendentes} label="Sem parecer" icon="ri-file-text-line" color="caution" />
      </div>

      {/* Filters */}
      <InspectionFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClear={handleClearFilters}
        totalResults={filteredVistorias.length}
      />

      {/* Main content */}
      <div className="flex flex-col lg:flex-row gap-5">
        <div className="flex-1 min-w-0 space-y-4">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "table" && <InspectionTable vistorias={filteredVistorias} onSelect={setSelectedVistoria} onAction={handleAction} />}
          {activeTab === "timeline" && <InspectionTimeline vistorias={filteredVistorias} onSelect={setSelectedVistoria} />}
          {activeTab === "cycles" && <MonitoringCycleCard cycles={ciclosMonitoramento} />}
          {activeTab === "comparison" && <TemporalComparisonPanel comparisons={comparacaoTemporal} />}
        </div>

        {showPriority && (
          <div className="lg:w-[340px] shrink-0 space-y-4">
            <InspectionPriorityCard />
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      <InspectionDetailDrawer vistoria={selectedVistoria} onClose={() => setSelectedVistoria(null)} onAction={handleAction} />

      {/* New Inspection Modal */}
      {showNewModal && <NewInspectionModal onClose={() => setShowNewModal(false)} />}
    </div>
  );
}